import { type ReactNode, useRef } from 'react';

import { Box, Divider, Stack } from '@mui/material';

// A minimal rich-text editor: a contentEditable body with a formatting toolbar,
// driven by document.execCommand (enough for a prototype — bold/italic/underline,
// lists and links). Owns its styling (a wrapped component). Uncontrolled; emits
// the current HTML via onChange. Remount (change `key`) to reset it.
const RichTextEditor = ({
  placeholder,
  minHeight = 200,
  onChange,
}: {
  placeholder?: string;
  minHeight?: number;
  onChange?: (html: string) => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const emit = () => onChange?.(ref.current?.innerHTML ?? '');
  const exec = (command: string, value?: string) => {
    ref.current?.focus();
    document.execCommand(command, false, value);
    emit();
  };
  const insertLink = () => {
    const url = window.prompt('Link URL');
    if (url) exec('createLink', url);
  };

  const tools: { label: string; glyph: ReactNode; run: () => void }[] = [
    {
      label: 'Bold',
      glyph: (
        <Box sx={{ fontWeight: 700 }} component='span'>
          B
        </Box>
      ),
      run: () => exec('bold'),
    },
    {
      label: 'Italic',
      glyph: (
        <Box sx={{ fontStyle: 'italic' }} component='span'>
          I
        </Box>
      ),
      run: () => exec('italic'),
    },
    {
      label: 'Underline',
      glyph: (
        <Box component='span' sx={{ textDecoration: 'underline' }}>
          U
        </Box>
      ),
      run: () => exec('underline'),
    },
    {
      label: 'Bulleted list',
      glyph: '•',
      run: () => exec('insertUnorderedList'),
    },
    {
      label: 'Numbered list',
      glyph: '1.',
      run: () => exec('insertOrderedList'),
    },
    { label: 'Insert link', glyph: 'Link', run: insertLink },
  ];

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Stack
        sx={{ alignItems: 'center', px: 1, py: 0.5 }}
        direction='row'
        spacing={0.5}
      >
        {tools.map((t) => (
          <Box
            key={t.label}
            component='button'
            type='button'
            aria-label={t.label}
            title={t.label}
            // Keep the editor's selection while the toolbar is clicked.
            onMouseDown={(e) => e.preventDefault()}
            onClick={t.run}
            sx={{
              border: 0,
              background: 'none',
              cursor: 'pointer',
              minWidth: 28,
              height: 28,
              px: 1,
              borderRadius: 1,
              font: 'inherit',
              fontSize: 14,
              color: 'text.secondary',
              '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
            }}
          >
            {t.glyph}
          </Box>
        ))}
      </Stack>
      <Divider />
      <Box
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        role='textbox'
        aria-multiline='true'
        onInput={emit}
        data-placeholder={placeholder}
        sx={{
          minHeight,
          p: 1.5,
          outline: 'none',
          overflow: 'auto',
          typography: 'body1',
          '&:empty::before': {
            content: 'attr(data-placeholder)',
            color: 'text.disabled',
          },
          '& ul, & ol': { pl: 3, m: 0 },
        }}
      />
    </Box>
  );
};

export default RichTextEditor;
